/**
 * Fuzzy Search Utility
 * 
 * Provides typo-tolerant search matching using a combination of:
 * 1. Exact substring match (fastest path)
 * 2. Levenshtein edit distance (handles character swaps, insertions, deletions)
 * 3. Bigram similarity (handles partial matches and reordered characters)
 * 
 * Usage:
 *   import { fuzzyMatch, fuzzyFilter } from '../lib/fuzzySearch';
 * 
 *   // Check a single string
 *   fuzzyMatch('spnach', 'Spinach') → true
 * 
 *   // Filter an array of objects
 *   fuzzyFilter(products, searchQuery, ['name', 'description'])
 */

/**
 * Compute Levenshtein edit distance between two strings.
 * Returns the minimum number of single-character edits (insert, delete, substitute)
 * needed to change one string into the other.
 */
function levenshtein(a, b) {
  const la = a.length;
  const lb = b.length;

  // Optimization: early exit for trivial cases
  if (la === 0) return lb;
  if (lb === 0) return la;
  if (a === b) return 0;

  // Use single-row DP for O(min(m,n)) space
  const prev = Array.from({ length: lb + 1 }, (_, i) => i);

  for (let i = 1; i <= la; i++) {
    let corner = i - 1;
    prev[0] = i;
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const temp = prev[j];
      prev[j] = Math.min(
        prev[j] + 1,        // deletion
        prev[j - 1] + 1,    // insertion
        corner + cost        // substitution
      );
      corner = temp;
    }
  }

  return prev[lb];
}

/**
 * Generate character bigrams (pairs) from a string.
 * e.g., "spinach" → ["sp","pi","in","na","ac","ch"]
 */
function bigrams(str) {
  const result = [];
  for (let i = 0; i < str.length - 1; i++) {
    result.push(str.slice(i, i + 2));
  }
  return result;
}

/**
 * Sørensen–Dice coefficient based on bigrams.
 * Returns a value between 0 (no similarity) and 1 (identical).
 */
function bigramSimilarity(a, b) {
  if (a.length < 2 || b.length < 2) {
    // For very short strings, fall back to character overlap
    const setA = new Set(a.split(''));
    const setB = new Set(b.split(''));
    let overlap = 0;
    for (const ch of setA) if (setB.has(ch)) overlap++;
    return (2 * overlap) / (setA.size + setB.size) || 0;
  }

  const bigramsA = bigrams(a);
  const bigramsB = bigrams(b);
  const setB = new Set(bigramsB);

  let matches = 0;
  for (const bg of bigramsA) {
    if (setB.has(bg)) matches++;
  }

  return (2 * matches) / (bigramsA.length + bigramsB.length);
}

/**
 * Check if the query matches against every word in a multi-word search
 * by splitting on spaces and checking each word independently.
 */
function matchesAllWords(query, target) {
  const words = query.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return null; // single word, skip this check
  return words.every(word => singleFuzzyMatch(word, target));
}

/**
 * Core single-word fuzzy match.
 * Returns true if `query` is a fuzzy match against `target`.
 */
function singleFuzzyMatch(query, target) {
  const q = query.toLowerCase().trim();
  const t = target.toLowerCase().trim();

  if (!q) return true; // empty query matches everything
  if (!t) return false;

  // 1. Exact substring — fastest path
  if (t.includes(q)) return true;

  // 2. Check against each word in the target independently
  const targetWords = t.split(/\s+/);
  for (const word of targetWords) {
    // Allow edit distance proportional to word length
    // Short words (≤3 chars): 1 edit allowed
    // Medium words (4-6 chars): 1-2 edits allowed
    // Long words (7+ chars): up to ~30% of query length
    const maxDist = q.length <= 3 ? 1 : q.length <= 6 ? 2 : Math.ceil(q.length * 0.3);
    const dist = levenshtein(q, word);
    if (dist <= maxDist) return true;

    // Also check if query is a fuzzy prefix of the word
    if (word.length > q.length) {
      const prefix = word.slice(0, q.length);
      if (levenshtein(q, prefix) <= Math.max(1, Math.floor(q.length * 0.25))) return true;
    }
  }

  // 3. Bigram similarity against the full target
  const similarity = bigramSimilarity(q, t);
  if (similarity >= 0.55) return true;

  // 4. Check if query chars appear in order (subsequence match)
  //    Handles cases like "spi" matching "spinach" even with a typo in between
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  if (qi >= q.length * 0.75) return true;

  return false;
}

// ─── Romanized Hindi → English Alias Dictionary ───────────────────────────
// Maps common Romanized Hindi (and regional/colloquial) terms to their
// English equivalents. This allows Indian users to type in Hindi transliteration
// and still find the correct English-named products.
//
// Each key is a lowercase Romanized Hindi term. The value is an array of
// English words that should be considered matches. The fuzzy engine is also
// applied to the alias lookup itself, so "palk" will match "palak" which
// resolves to "spinach".
//
// Actual products in DB:
//   Green Butterhead, Red Butterhead, Lollo Rosso, Curly Kale, Pak Choi,
//   Romaine Lettuce, Green Oak Lettuce, Red Oak Lettuce, Cabbage, Broccoli,
//   Cauliflower, Cherry Tomato, Tomato, Zucchini, Green Capsicum, Bell Pepper,
//   Radish, Carrot, Onion, Potato, Turnip, peppers, spinach, Carrots,
//   Lettuce, Mushroom, Sugarcane, Mango
//
// To add new products: just add more entries to this dictionary.
// ───────────────────────────────────────────────────────────────────────────

const HINDI_ALIASES = {

  // ── Lettuce varieties (Butterhead, Romaine, Oak, Lollo Rosso) ──
  'salad patta':    ['lettuce', 'butterhead', 'romaine', 'oak'],
  'salad':          ['lettuce', 'butterhead', 'romaine', 'oak'],
  'patta':          ['lettuce', 'butterhead', 'oak', 'leafy'],
  'lettus':         ['lettuce'],

  // ── Spinach ──
  'palak':          ['spinach'],
  'paalak':         ['spinach'],

  // ── Cabbage ──
  'patta gobhi':    ['cabbage'],
  'patta gobi':     ['cabbage'],
  'band gobhi':     ['cabbage'],
  'band gobi':      ['cabbage'],
  'gobhi':          ['cabbage', 'cauliflower'],
  'gobi':           ['cabbage', 'cauliflower'],

  // ── Cauliflower ──
  'phool gobhi':    ['cauliflower'],
  'phool gobi':     ['cauliflower'],
  'fool gobhi':     ['cauliflower'],
  'fool gobi':      ['cauliflower'],

  // ── Broccoli ──
  'hari gobhi':     ['broccoli'],
  'hari gobi':      ['broccoli'],
  'brokli':         ['broccoli'],
  'brocli':         ['broccoli'],

  // ── Kale ──
  'karam saag':     ['kale', 'curly kale'],
  'saag':           ['kale', 'spinach', 'leafy', 'greens'],

  // ── Pak Choi / Bok Choy ──
  'bok choy':       ['pak choi'],
  'chinese patta':  ['pak choi'],
  'chinese saag':   ['pak choi'],

  // ── Tomato / Cherry Tomato ──
  'tamatar':        ['tomato', 'cherry tomato'],
  'tamaatar':       ['tomato', 'cherry tomato'],
  'chhota tamatar': ['cherry tomato'],
  'cherry tamatar': ['cherry tomato'],

  // ── Bell Pepper / Green Capsicum / Peppers ──
  'shimla mirch':   ['bell pepper', 'capsicum', 'peppers'],
  'shimla':         ['bell pepper', 'capsicum', 'peppers'],
  'mirch':          ['pepper', 'peppers', 'capsicum'],
  'mirchi':         ['pepper', 'peppers', 'capsicum'],
  'capsicum':       ['green capsicum', 'bell pepper', 'peppers'],
  'hari mirch':     ['green capsicum', 'peppers'],

  // ── Zucchini ──
  'zukini':         ['zucchini'],
  'turai':          ['zucchini'],
  'torai':          ['zucchini'],
  'ghia':           ['zucchini'],

  // ── Radish ──
  'mooli':          ['radish'],
  'muli':           ['radish'],
  'moola':          ['radish'],

  // ── Carrot ──
  'gajar':          ['carrot', 'carrots'],
  'gaajar':         ['carrot', 'carrots'],

  // ── Onion ──
  'pyaaz':          ['onion'],
  'pyaz':           ['onion'],
  'pyaj':           ['onion'],
  'kanda':          ['onion'],

  // ── Potato ──
  'aloo':           ['potato'],
  'aaloo':          ['potato'],
  'alu':            ['potato'],
  'batata':         ['potato'],

  // ── Turnip ──
  'shalgam':        ['turnip'],
  'shaljam':        ['turnip'],

  // ── Mushroom ──
  'khumb':          ['mushroom'],
  'khumbi':         ['mushroom'],
  'guchi':          ['mushroom'],
  'dhingri':        ['mushroom'],
  'kukurmutta':     ['mushroom'],

  // ── Sugarcane ──
  'ganna':          ['sugarcane'],
  'ganne':          ['sugarcane'],
  'ikshu':          ['sugarcane'],

  // ── Mango ──
  'aam':            ['mango'],
  'keri':           ['mango'],
  'kairi':          ['mango'],

  // ── Generic produce terms ──
  'sabzi':          ['vegetable', 'vegetables', 'greens', 'capsicum', 'pepper'],
  'sabji':          ['vegetable', 'vegetables', 'greens', 'capsicum', 'pepper'],
  'subzi':          ['vegetable', 'vegetables', 'greens', 'capsicum', 'pepper'],
  'bhaji':          ['vegetable', 'vegetables', 'greens'],
  'hari sabzi':     ['greens', 'leafy', 'vegetables', 'spinach', 'lettuce'],
  'hara':           ['green', 'greens'],
  'lal':            ['red'],
  'taza':           ['fresh'],
  'phal':           ['mango', 'tomato', 'fruit'],
  'kand':           ['potato', 'turnip', 'onion', 'radish', 'carrot'],
  'jad':            ['potato', 'turnip', 'onion', 'radish', 'carrot'],
};

// Pre-build a flattened lookup: maps each alias key to its English expansions.
// Also build a reverse index: English word → all Hindi aliases that map to it.
const _aliasKeys = Object.keys(HINDI_ALIASES);

/**
 * Expand a query using Hindi aliases.
 * If any word in the query fuzzy-matches a Hindi alias key,
 * returns additional English search terms to try.
 *
 * @param {string} query - The user's search input
 * @returns {string[]} Array of English equivalents (empty if no alias matched)
 */
function expandHindiAliases(query) {
  const qLower = query.toLowerCase().trim();
  if (!qLower) return [];

  // 1. Try the full query as a multi-word alias (exact match)
  //    If found, return ONLY this expansion — do NOT split into individual words.
  //    "patta gobhi" → ["cabbage"], NOT "patta"→lettuce + "gobhi"→cabbage
  if (HINDI_ALIASES[qLower]) {
    return [...HINDI_ALIASES[qLower]];
  }

  // 2. Try full query as fuzzy match against multi-word alias keys
  const multiWordKeys = _aliasKeys.filter(k => k.includes(' '));
  for (const aliasKey of multiWordKeys) {
    const shorter = Math.min(qLower.length, aliasKey.length);
    const lenDiff = Math.abs(qLower.length - aliasKey.length);
    const maxDist = shorter <= 6 ? 1 : Math.ceil(shorter * 0.2);
    if (lenDiff <= maxDist && levenshtein(qLower, aliasKey) <= maxDist) {
      return [...HINDI_ALIASES[aliasKey]];
    }
  }

  // 3. Single-word query or no multi-word alias matched — try per-word expansion
  const words = qLower.split(/\s+/);
  const expansions = [];

  for (const word of words) {
    // Exact alias match
    if (HINDI_ALIASES[word]) {
      expansions.push(...HINDI_ALIASES[word]);
      continue;
    }

    // Fuzzy alias match — allow typos in the Hindi term itself
    for (const aliasKey of _aliasKeys) {
      if (aliasKey.includes(' ')) continue;

      const shorter = Math.min(word.length, aliasKey.length);
      const lenDiff = Math.abs(word.length - aliasKey.length);
      const maxDist = shorter <= 3 ? 1 : shorter <= 6 ? Math.min(2, shorter - 2) : Math.ceil(shorter * 0.25);
      if (lenDiff > maxDist) continue;

      if (levenshtein(word, aliasKey) <= maxDist) {
        expansions.push(...HINDI_ALIASES[aliasKey]);
      }
    }
  }

  return [...new Set(expansions)];
}

/**
 * Score how well a query matches a target string.
 * Returns a numeric score (lower = better). Returns -1 if no match at all.
 *
 * Score tiers:
 *   0   = exact full match (query === target)
 *   1   = exact substring (target contains query)
 *   2   = Hindi alias — exact alias key matched, and expansion is an exact substring of target
 *   3   = Hindi alias — fuzzy alias key matched, and expansion is an exact substring of target
 *   4   = Hindi alias — exact alias key matched, expansion fuzzy-matches target
 *   5   = Hindi alias — fuzzy alias key matched, expansion fuzzy-matches target
 *   10  = Levenshtein distance match (close English typo)
 *   15  = Fuzzy prefix match
 *   20  = Bigram similarity match
 *   25  = Subsequence match
 *   -1  = no match
 *
 * @param {string} query - The user's search input
 * @param {string} target - The string to match against
 * @returns {number} Match score (lower = better, -1 = no match)
 */
export function fuzzyScore(query, target) {
  if (!query || !target) return !query ? 0 : -1;

  const q = query.trim().toLowerCase();
  const t = target.trim().toLowerCase();

  if (!q) return 0;
  if (!t) return -1;

  // ── Tier 0: Exact full match ──
  if (q === t) return 0;

  // ── Tier 1: Exact substring ──
  if (t.includes(q)) return 1;

  // ── Multi-word: check if all words match (use best single-word score) ──
  const queryWords = q.split(/\s+/).filter(Boolean);
  if (queryWords.length > 1) {
    const wordScores = queryWords.map(word => singleEnglishScore(word, t));
    if (wordScores.every(s => s !== -1)) {
      return Math.max(...wordScores); // worst word score determines overall
    }
  }

  // ── Single word: English fuzzy score ──
  const englishResult = singleEnglishScore(q, t);

  // ── Hindi alias expansion ──
  const hindiResult = hindiAliasScore(query, t);

  // Return the best (lowest) score from either path
  if (englishResult === -1 && hindiResult === -1) return -1;
  if (englishResult === -1) return hindiResult;
  if (hindiResult === -1) return englishResult;
  return Math.min(englishResult, hindiResult);
}

/**
 * Score a single English word against the target (no alias expansion).
 * @returns {number} Score (10-25 range) or -1 if no match.
 */
function singleEnglishScore(q, t) {
  // Already handled: exact substring (tier 1) is checked in fuzzyScore before this

  const targetWords = t.split(/\s+/);
  let bestDist = Infinity;

  for (const word of targetWords) {
    const maxDist = q.length <= 3 ? 1 : q.length <= 6 ? 2 : Math.ceil(q.length * 0.3);
    const dist = levenshtein(q, word);
    if (dist <= maxDist) {
      bestDist = Math.min(bestDist, dist);
    }

    // Fuzzy prefix
    if (word.length > q.length) {
      const prefix = word.slice(0, q.length);
      if (levenshtein(q, prefix) <= Math.max(1, Math.floor(q.length * 0.25))) {
        return 15;
      }
    }
  }

  if (bestDist !== Infinity) return 10 + bestDist; // 10-12 range

  // Bigram similarity
  if (bigramSimilarity(q, t) >= 0.55) return 20;

  // Subsequence
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  if (qi >= q.length * 0.75) return 25;

  return -1;
}

/**
 * Score based on Hindi alias expansion.
 * @returns {number} Score (2-5 range) or -1 if no alias match.
 */
function hindiAliasScore(query, target) {
  const qLower = query.toLowerCase().trim();
  if (!qLower) return -1;

  const tLower = target.toLowerCase();
  let bestScore = -1;

  const checkExpansions = (expansions, tier) => {
    for (const eng of expansions) {
      const engLower = eng.toLowerCase();
      if (tLower.includes(engLower)) {
        bestScore = bestScore === -1 ? tier : Math.min(bestScore, tier);
      } else {
        // Strict fuzzy: only Levenshtein ≤ 1 against individual target words
        const targetWords = tLower.split(/\s+/);
        for (const tw of targetWords) {
          if (levenshtein(engLower, tw) <= 1) {
            bestScore = bestScore === -1 ? (tier + 2) : Math.min(bestScore, tier + 2);
            break;
          }
        }
      }
    }
  };

  // 1. Try full query as exact multi-word alias key
  //    If found, use ONLY this expansion — do NOT also split into words.
  if (HINDI_ALIASES[qLower]) {
    checkExpansions(HINDI_ALIASES[qLower], 2);
    return bestScore;
  }

  // 2. Try full query as fuzzy match against multi-word alias keys
  const multiWordKeys = _aliasKeys.filter(k => k.includes(' '));
  for (const aliasKey of multiWordKeys) {
    const shorter = Math.min(qLower.length, aliasKey.length);
    const lenDiff = Math.abs(qLower.length - aliasKey.length);
    const maxDist = shorter <= 6 ? 1 : Math.ceil(shorter * 0.2);
    if (lenDiff <= maxDist && levenshtein(qLower, aliasKey) <= maxDist) {
      checkExpansions(HINDI_ALIASES[aliasKey], 3);
      return bestScore;
    }
  }

  // 3. Single-word query or no multi-word alias matched — try per-word
  const words = qLower.split(/\s+/);
  for (const word of words) {
    if (HINDI_ALIASES[word]) {
      checkExpansions(HINDI_ALIASES[word], 2);
      continue;
    }

    for (const aliasKey of _aliasKeys) {
      if (aliasKey.includes(' ')) continue;

      const shorter = Math.min(word.length, aliasKey.length);
      const lenDiff = Math.abs(word.length - aliasKey.length);
      const maxDist = shorter <= 3 ? 1 : shorter <= 6 ? Math.min(2, shorter - 2) : Math.ceil(shorter * 0.25);
      if (lenDiff > maxDist) continue;

      if (levenshtein(word, aliasKey) <= maxDist) {
        checkExpansions(HINDI_ALIASES[aliasKey], 3);
      }
    }
  }

  return bestScore;
}

/**
 * Boolean wrapper — check if a search query fuzzy-matches a target string.
 * Handles multi-word queries, typo tolerance, AND Romanized Hindi aliases.
 * 
 * @param {string} query - The user's search input (possibly misspelled or in Hindi)
 * @param {string} target - The string to match against
 * @returns {boolean} Whether the query is a fuzzy match for the target
 * 
 * @example
 *   fuzzyMatch('aaloo', 'Potato')              → true  (Hindi alias, score 2)
 *   fuzzyMatch('aaloo', 'Tomato')              → false (no longer matches!)
 *   fuzzyMatch('palak', 'spinach')             → true  (Hindi alias)
 *   fuzzyMatch('tamatar', 'Cherry Tomato')     → true  (Hindi alias)
 *   fuzzyMatch('shimla mirch', 'Bell Pepper')  → true  (multi-word Hindi alias)
 *   fuzzyMatch('spnach', 'spinach')            → true  (English typo tolerance)
 */
export function fuzzyMatch(query, target) {
  return fuzzyScore(query, target) !== -1;
}

/**
 * Filter an array of objects using fuzzy search across one or more fields.
 * Returns items sorted by match quality (best matches first).
 * Hindi alias matches always rank above loose fuzzy English matches.
 * 
 * @param {Array<Object>} items - Array of objects to filter
 * @param {string} query - The user's search input
 * @param {string|string[]} fields - Field name(s) to search in
 * @returns {Array<Object>} Filtered and sorted array
 */
export function fuzzyFilter(items, query, fields) {
  if (!query || !query.trim()) return items;
  if (!Array.isArray(items)) return [];

  const fieldList = Array.isArray(fields) ? fields : [fields];

  const scored = items
    .map(item => {
      let bestScore = -1;

      for (const field of fieldList) {
        const value = field.split('.').reduce((obj, key) => obj?.[key], item);
        if (typeof value !== 'string') continue;

        const score = fuzzyScore(query, value);
        if (score !== -1) {
          bestScore = bestScore === -1 ? score : Math.min(bestScore, score);
        }
      }

      return { item, score: bestScore };
    })
    .filter(entry => entry.score !== -1);

  // Sort by match quality: lower score = better match
  scored.sort((a, b) => a.score - b.score);

  return scored.map(entry => entry.item);
}